import { Platform } from '@angular/cdk/platform';
import { NgOptimizedImage } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRippleModule } from '@angular/material/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { environment } from '../../../../environments/environment';
import { SendResetPasswordEmailDialogComponent } from '../../../components/auth/send-reset-password-email-dialog/send-reset-password-email-dialog.component';
import { Button } from '../../../components/common/button/button';
import { User } from '../../../models/user';
import { AuthApiService } from '../../../services/apis/auth-api.service';
import { ApiException, ApiStatus } from '../../../services/apis/status';
import { AuthService } from '../../../services/auth.service';
import { CartService } from '../../../services/cart.service';
import { GoogleAuthService } from '../../../services/google-auth.service';
import { MessageService } from '../../../services/message.service';
import { TitleService } from '../../../services/title.service';
import { validateFormGroup, validateNonEmptyString } from '../../../utils/form';
declare const google: any; // 声明 google 对象

@Component({
  selector: 'app-login-page',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatRippleModule,
    MatCheckboxModule,
    RouterModule,
    Button,
    MatDialogModule,
    MatDividerModule,
    NgOptimizedImage,
    MatRippleModule,
    Button,
  ],
  templateUrl: './login-page.html',
  styleUrl: './login-page.scss',
})
export class LoginPage implements OnInit {
  formGroup: FormGroup;

  constructor(
    private titleService: TitleService,
    private fb: FormBuilder,
    private authApi: AuthApiService,
    private messageService: MessageService,
    private router: Router,
    private authService: AuthService,
    private cartService: CartService,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private platform: Platform,
    private googleAuthService: GoogleAuthService
  ) {
    this.titleService.setTitle('Login');
    this.formGroup = this.fb.group({
      email: [
        '',
        [
          Validators.required,
          Validators.pattern(
            /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
          ),
        ],
      ],
      password: ['', [Validators.required, validateNonEmptyString]],
      remember: [false],
    });
  }

  ngOnInit(): void {
    if (this.platform.isBrowser) {
      const email = localStorage.getItem('user.email');
      if (email) {
        this.formGroup.patchValue({
          email,
          remember: true,
        });
      }
    }
  }

  ngAfterViewInit(): void {
    if (this.platform.isBrowser) {
      this.googleAuthService.loadGoogleScript().then(() => {
        this.initializeGoogleSignIn();
      });
    }
  }

  initializeGoogleSignIn(): void {
    google.accounts.id.initialize({
      client_id: environment.google.oauth.clientID, // 替换为你的客户端 ID
      callback: this.onGoogleSignIn.bind(this),
      use_fedcm_for_prompt: true, // 启用 FedCM
      use_fedcm_for_button: false,
    });
    // 显示 One Tap 提示或按钮
  }

  async onGoogleSignIn(v: any) {
    const idToken = v.credential;
    try {
      this.loading = true;
      const r = await this.authApi.loginWithGoogle(idToken);
      this.afterLogin(r);
    } catch (error) {
    } finally {
      this.loading = false;
    }
  }

  loginWithGoogle() {
    google.accounts.id.prompt((notification: any) => {});
  }

  loading = false;
  submit(e: SubmitEvent) {
    if (this.loading) {
      return;
    }
    const values = validateFormGroup(this.formGroup);
    if (!values) {
      return;
    }
    this.login(values);
  }

  async login(values: any) {
    try {
      this.loading = true;
      const r = await this.authApi.login(
        values.email,
        values.password,
        values.remember
      );
      if (values.remember) {
        localStorage.setItem('user.email', values.email);
      } else {
        localStorage.removeItem('user.email');
      }
      this.afterLogin(r);
    } catch (error) {
      if (error instanceof ApiException) {
        if (error.status === ApiStatus.USER_EMAIL_NOT_FOUND) {
          this.messageService
            .open('Email not found', 'Signup')
            ?.onAction()
            .subscribe(() => {
              this.router.navigate(['/signup']);
            });
        } else if (error.status === ApiStatus.USER_PASSWORD_INCORRECT) {
          this.messageService.open('Password incorrect', 'OK');
        } else if (error.status === ApiStatus.USER_BANNED) {
          this.messageService.open('Sorry, this account has been banned', 'OK');
        } else if (error.status === ApiStatus.USER_EMAIL_NOT_VERIFIED) {
          this.messageService.open('Please verify your email first', 'OK');
        } else if (error.status !== ApiStatus.OK) {
          this.messageService.open('Unknown Error', 'OK');
        }
      }
    } finally {
      this.loading = false;
    }
  }
  forgotPassword() {
    this.dialog.open(SendResetPasswordEmailDialogComponent, {
      width: '480px',
      autoFocus: false,
      data: this.formGroup.get('email')?.value,
    });
  }

  afterLogin(data: User) {
    this.authService.profile.set(data);
    this.cartService.findShoppingCartItems();
    const url = this.route.snapshot.queryParams['r'] || '/';
    try {
      this.router.navigateByUrl(url, { replaceUrl: true });
    } catch (error) {
      this.router.navigateByUrl('/', { replaceUrl: true });
    }
  }
}
